import React, {FC, useState} from 'react';
import {Form, Field, Formik, FormikProps} from 'formik';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {SelectChangeEvent} from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import RedirectSvgIcon from 'assets/icons/buttons/RedirectSvgIcon';

import {FormFieldMoveRequestFormType, FormPageTypes, FormSectionTypes} from 'appRedux/actions/forms/types';
import {RootReducer} from 'appRedux/reducers';

import AgentSaveButton from 'components/AgentScreenComponents/_buttons/AgentSaveButton';
import FormikSelectInput from 'components/AgentScreenComponents/_form/FormBuilderSelectInput';
import {
    CurrentFieldTypes,
    getCurrentFieldInformation,
    getPagesWithHigherPriorities,
    getSectionsWithHigherPriorities,
    getInitialSectionsList,
} from 'components/Forms/FormBuilder/FieldForm/helper';

import {FORM_FIELD_TEMPLATE} from 'pages/admin/updateForm/partials/FormStructure/helper';

import {preventSendForm} from 'helpers/index';

interface MoveFormType {
    pageId: number;
    currentSectionId: number;
    fieldId?: number;
    onSubmitClick: (values: FormFieldMoveRequestFormType) => void;
}

const MoveForm: FC<MoveFormType> = ({pageId, currentSectionId, fieldId, onSubmitClick}) => {
    const [t] = useTranslation();

    const {
        admin: {
            formInfo: {pages},
        },
    } = useSelector<RootReducer>((state: RootReducer) => state) as RootReducer;

    const [sections, setSections] = useState<FormSectionTypes[]>(getInitialSectionsList(pages, fieldId));

    const onPageSelected = (event: SelectChangeEvent) => {
        const pageId = Number(event.target.value);
        const page = pages.find(page => page.id === pageId);
        if (page) {
            setSections(page.sections);
        }
    };

    const currentFieldInformation: CurrentFieldTypes | null = fieldId
        ? getCurrentFieldInformation(pages, fieldId)
        : null;

    const currentField = currentFieldInformation ? currentFieldInformation.field : null;

    const initialPageId = currentFieldInformation ? currentFieldInformation.page.id : pageId;
    const initialSectionId = currentFieldInformation ? currentFieldInformation.section.id : currentSectionId;

    return (
        <Formik
            initialValues={{
                pageId: initialPageId,
                sectionId: initialSectionId,
            }}
            onSubmit={onSubmitClick}
            enableReinitialize
        >
            {(formik: FormikProps<FormFieldMoveRequestFormType>) => {
                const filteredPages =
                    currentField && currentField.type === FORM_FIELD_TEMPLATE
                        ? getPagesWithHigherPriorities(pages, formik.values.pageId)
                        : pages;
                const filteredSections =
                    currentField && currentField.type === FORM_FIELD_TEMPLATE && initialPageId === formik.values.pageId
                        ? getSectionsWithHigherPriorities(sections, formik.values.sectionId)
                        : sections;
                return (
                    <Form onKeyDown={preventSendForm}>
                        {currentField && currentField.type === FORM_FIELD_TEMPLATE && (
                            <Alert sx={{mb: 2}} severity={'info'}>
                                <Typography>{t('orguser.forms.moveFieldTemplateTypeWarning')}</Typography>
                            </Alert>
                        )}
                        <Field
                            as="select"
                            required
                            name={'pageId'}
                            label={t('orguser.forms.formField.selectPage')}
                            options
                            component={FormikSelectInput}
                            onChange={e => {
                                onPageSelected(e);
                                formik.handleChange(e);
                            }}
                            fullwidth
                        >
                            {filteredPages.map((page: FormPageTypes) => {
                                const {title, id} = page;
                                return (
                                    <MenuItem key={`page-${id}`} value={id}>
                                        <Typography>{title}</Typography>
                                    </MenuItem>
                                );
                            })}
                        </Field>
                        {sections && sections.length > 0 && (
                            <Field
                                as="select"
                                required
                                name={'sectionId'}
                                label={t('orguser.forms.formField.selectSection')}
                                options
                                component={FormikSelectInput}
                                onChange={formik.handleChange}
                                fullwidth
                            >
                                {filteredSections.map((item: FormSectionTypes, index: number) => {
                                    const {title, id} = item;
                                    return (
                                        <MenuItem key={`menuItem-${index}`} value={id}>
                                            <Typography>{title}</Typography>
                                        </MenuItem>
                                    );
                                })}
                            </Field>
                        )}
                        <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', mt: 2}}>
                            <AgentSaveButton
                                isSubmit
                                isDisabled={!formik.values.pageId || !formik.values.sectionId}
                                icon={<RedirectSvgIcon isActive />}
                                title={t('common.buttons.move')}
                            />
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default MoveForm;
